import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import type {
  FeatureDetail,
  FeaturesResponse,
  FlowResponse,
  MixResponse,
  ProdResponse,
  SprintDetailsResponse,
  SprintPeriodsResponse,
  SummaryResponse,
  SyncStatusResponse,
  SprintsResponse,
  TeamsResponse,
} from '@internal/backstage-plugin-delivery-common';
import { apiError } from '@internal/ui';

export interface ProductivityQuery {
  days: number;
  /** Only this team (`No team` for the features without one). */
  team?: string;
  /** Only this Jira issue type. */
  type?: string;
  /** One sprint (its id) instead of the last `days` days. */
  sprint?: number;
}

export interface SprintQuery {
  /** Sprints shown, the latest ones. */
  count: number;
  /** Sprints between two ids (both included) instead of the last `count`. */
  from?: number;
  to?: number;
  /** Only this team (`No team` for the tickets without one). */
  team?: string;
  /** Only these Jira issue types, comma-separated. */
  type?: string;
  /** One sprint (details only). */
  sprint?: number;
}

/** The BI tabs (Mise en prod, Flux, Mix & qualité, Synthèse): a window, a team, types. */
export interface BiQuery {
  days: number;
  team?: string;
  /** Jira issue types, comma-separated. */
  type?: string;
  /** One sprint (its id) instead of the last `days` days. */
  sprint?: number;
}

export interface ProductivityApi {
  getTeams(query: ProductivityQuery): Promise<TeamsResponse>;
  getFeatures(query: ProductivityQuery): Promise<FeaturesResponse>;
  getFeature(key: string): Promise<FeatureDetail>;
  /** Tickets finished per sprint, type and team. */
  getSprints(query: SprintQuery): Promise<SprintsResponse>;
  /** The tickets behind a sprint, team or type, with their pull requests and runs. */
  getSprintTickets(query: SprintQuery): Promise<SprintDetailsResponse>;
  /** Tickets reaching production, what waits for it, the release cadence. */
  getProd(query: BiQuery): Promise<ProdResponse>;
  /** Phases, running sprint, work in progress, carry-over, aging pull requests. */
  getFlow(query: BiQuery): Promise<FlowResponse>;
  /** Mix of the work per type, created vs finished, bugs, hotfixes, CI failures. */
  getMix(query: BiQuery): Promise<MixResponse>;
  /** Headline figures of the other tabs and every team across them. */
  getSummary(query: BiQuery): Promise<SummaryResponse>;
  /** The sprints a tab can show instead of a number of days, newest first. */
  getSprintPeriods(): Promise<SprintPeriodsResponse>;
  /** How each data source stands: when it last refreshed, whether it failed. */
  getSyncStatus(): Promise<SyncStatusResponse>;
}

export const productivityApiRef = createApiRef<ProductivityApi>({
  id: 'plugin.productivity.service',
});

const params = ({ days, team, type, sprint }: ProductivityQuery) => {
  const search = new URLSearchParams({ days: String(days) });
  if (team) search.set('team', team);
  if (type) search.set('type', type);
  if (sprint !== undefined) search.set('sprint', String(sprint));
  return search.toString();
};

const sprintParams = ({ count, from, to, team, type, sprint }: SprintQuery) => {
  const search = new URLSearchParams({ count: String(count) });
  if (from !== undefined) search.set('from', String(from));
  if (to !== undefined) search.set('to', String(to));
  if (team) search.set('team', team);
  if (type) search.set('type', type);
  if (sprint !== undefined) search.set('sprint', String(sprint));
  return search.toString();
};

/** The `/productivity/*` routes of the delivery backend: they read the database only. */
export class ProductivityClient implements ProductivityApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getJson<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('delivery');
    const res = await this.fetchApi.fetch(`${baseUrl}${path}`);
    if (!res.ok) {
      throw await apiError(res);
    }
    return res.json();
  }

  getTeams(query: ProductivityQuery) {
    return this.getJson<TeamsResponse>(`/productivity/teams?${params(query)}`);
  }

  getFeatures(query: ProductivityQuery) {
    return this.getJson<FeaturesResponse>(
      `/productivity/features?${params(query)}`,
    );
  }

  getSprints(query: SprintQuery) {
    return this.getJson<SprintsResponse>(
      `/productivity/sprints?${sprintParams(query)}`,
    );
  }

  getSprintTickets(query: SprintQuery) {
    return this.getJson<SprintDetailsResponse>(
      `/productivity/sprints/tickets?${sprintParams(query)}`,
    );
  }

  getProd(query: BiQuery) {
    return this.getJson<ProdResponse>(`/productivity/prod?${params(query)}`);
  }

  getFlow(query: BiQuery) {
    return this.getJson<FlowResponse>(`/productivity/flow?${params(query)}`);
  }

  getMix(query: BiQuery) {
    return this.getJson<MixResponse>(`/productivity/mix?${params(query)}`);
  }

  getSummary(query: BiQuery) {
    return this.getJson<SummaryResponse>(
      `/productivity/summary?${params(query)}`,
    );
  }

  getSprintPeriods() {
    return this.getJson<SprintPeriodsResponse>('/productivity/sprint-periods');
  }

  getSyncStatus() {
    return this.getJson<SyncStatusResponse>('/sync');
  }

  getFeature(key: string) {
    return this.getJson<FeatureDetail>(
      `/productivity/features/${encodeURIComponent(key.trim())}`,
    );
  }
}
